import { useCallback, useState } from 'react';

import useToast from '@/components/Toast/useToast';
import { subscriber } from '@/data/fetcher';
import { useOrganizationId } from '@/hooks/useSwr';
import { ObservableValue } from '@/hooks/useSwrObservable';
import {
  GenerateSyntheticExamplesBody,
  GenerateSyntheticExamplesResponse,
} from '@/types/schema';
import { apiDatasetsPath } from '@/utils/constants';

export function useGenerateSyntheticExamples(
  selectedExampleIds: string[],
  numExamplesToGenerate: number,
  selectionMethod: 'automatic' | 'manual',
  datasetId?: string | null
) {
  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const [data, setData] = useState<
    ObservableValue<GenerateSyntheticExamplesResponse>
  >({
    done: true,
    value: { examples: [] } as GenerateSyntheticExamplesResponse,
  });

  const generate = useCallback(() => {
    if (!datasetId) {
      createToast({
        title: 'Error',
        description: 'Dataset not found',
        error: true,
      });
    }
    subscriber<
      GenerateSyntheticExamplesResponse,
      never,
      GenerateSyntheticExamplesBody
    >(
      {
        url: `${apiDatasetsPath}/${datasetId}/generate`,
        method: 'POST',
        subscriberParser: 'jsonpatch',
        json: {
          num_examples: numExamplesToGenerate,
          example_ids:
            selectionMethod === 'automatic' ? [] : selectedExampleIds,
        },
        headers: { 'X-Tenant-Id': organizationId },
      },
      {
        next: function (error, data) {
          if (error) {
            let errorMessage = 'Failed to generate';
            try {
              const parsedError = JSON.parse(error.message);
              errorMessage = parsedError?.detail ?? errorMessage;
            } catch (e) {
              errorMessage = error.message || errorMessage;
            }
            createToast({
              title: 'Failed to generate',
              description: errorMessage,
              error: true,
            });
            return;
          }
          if (data) {
            if (typeof data === 'function') {
              setData((prev) => data(prev) ?? prev);
            } else {
              setData(data);
            }
          }
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numExamplesToGenerate, selectedExampleIds, datasetId, organizationId]);

  return {
    syntheticExamples: data.value?.examples ?? [],
    setSyntheticExamples: (v) =>
      setData({ done: true, value: { examples: v } }),
    generateTrigger: generate,
    isGenerateLoading: !data?.done,
  };
}
